export const MICROPHONE_CHECK_STATUS = Object.freeze({
  IDLE: 'IDLE',
  CHECKING: 'CHECKING',
  READY: 'READY',
  UNSUPPORTED: 'UNSUPPORTED',
  INSECURE: 'INSECURE',
  DENIED: 'DENIED',
  NO_DEVICE: 'NO_DEVICE',
  UNAVAILABLE: 'UNAVAILABLE',
  NO_SIGNAL: 'NO_SIGNAL',
  CHANGED: 'CHANGED',
  ERROR: 'ERROR',
  CANCELLED: 'CANCELLED',
})

const DEFAULT_SAMPLE_DURATION_MS = 3000
const SAMPLE_INTERVAL_MS = 60
const SIGNAL_THRESHOLD = 0.018

function classifyPlatform(navigatorValue) {
  const source = `${navigatorValue?.userAgentData?.platform || ''} ${navigatorValue?.platform || ''} ${navigatorValue?.userAgent || ''}`.toLowerCase()
  if (source.includes('android')) return 'ANDROID'
  if (source.includes('iphone') || source.includes('ipad') || source.includes('ipod')) return 'IOS'
  if (source.includes('win')) return 'WINDOWS'
  if (source.includes('mac')) return 'MACOS'
  if (source.includes('linux')) return 'LINUX'
  return 'OTHER'
}

function classifyBrowser(windowValue, navigatorValue) {
  const userAgent = String(navigatorValue?.userAgent || '')
  const appWebView = Boolean(
    windowValue?.ReactNativeWebView ||
    windowValue?.flutter_inappwebview ||
    /; wv\)/i.test(userAgent) ||
    /Version\/\d+\.\d+ Chrome\/\d+.*Mobile Safari/i.test(userAgent),
  )
  if (appWebView) return { browser: 'WEBVIEW', runtime: 'APP_WEBVIEW', browserMajor: null }

  const matchers = [
    ['EDGE', /(?:Edg|EdgiOS|EdgA)\/(\d+)/i],
    ['CHROME', /(?:Chrome|CriOS)\/(\d+)/i],
    ['FIREFOX', /(?:Firefox|FxiOS)\/(\d+)/i],
    ['SAFARI', /Version\/(\d+).+Safari/i],
  ]
  for (const [browser, pattern] of matchers) {
    const match = userAgent.match(pattern)
    if (match) {
      return { browser, runtime: 'WEB', browserMajor: Number(match[1]) || null }
    }
  }
  return { browser: 'OTHER', runtime: 'WEB', browserMajor: null }
}

export function getDeviceEnvironment({
  browserWindow = typeof window === 'undefined' ? null : window,
  browserNavigator = typeof navigator === 'undefined' ? null : navigator,
  speechRecognition = null,
} = {}) {
  const browser = classifyBrowser(browserWindow, browserNavigator)
  return {
    platform: classifyPlatform(browserNavigator),
    browser: browser.browser,
    browserMajor: browser.browserMajor,
    runtime: browser.runtime,
    speechRecognitionSupported: Boolean(speechRecognition),
    mediaDevicesSupported: Boolean(
      browserNavigator?.mediaDevices?.getUserMedia && browserNavigator?.mediaDevices?.enumerateDevices,
    ),
    audioContextSupported: Boolean(browserWindow?.AudioContext || browserWindow?.webkitAudioContext),
  }
}

export function buildMicrophoneCheckPayload({
  check,
  browserWindow = typeof window === 'undefined' ? null : window,
  browserNavigator = typeof navigator === 'undefined' ? null : navigator,
  speechRecognition = null,
} = {}) {
  const environment = getDeviceEnvironment({ browserWindow, browserNavigator, speechRecognition })
  const status = String(check?.status || MICROPHONE_CHECK_STATUS.ERROR).toUpperCase()
  return {
    status,
    ...environment,
    audioInputCount: Math.max(0, Math.min(20, Number(check?.audioInputCount) || 0)),
    deviceLabelPresent: Boolean(check?.deviceLabel),
    signalDetected: status === MICROPHONE_CHECK_STATUS.READY,
    checkDurationMs: Math.max(0, Math.min(30000, Number(check?.checkDurationMs) || 0)),
  }
}

export function getMicrophoneSupport({
  browserWindow = typeof window === 'undefined' ? null : window,
  browserNavigator = typeof navigator === 'undefined' ? null : navigator,
  speechRecognition = null,
} = {}) {
  if (!browserWindow || !browserNavigator || !speechRecognition) {
    return { supported: false, status: MICROPHONE_CHECK_STATUS.UNSUPPORTED }
  }
  if (browserWindow.isSecureContext === false) {
    return { supported: false, status: MICROPHONE_CHECK_STATUS.INSECURE }
  }
  if (!browserNavigator.mediaDevices?.getUserMedia || !browserNavigator.mediaDevices?.enumerateDevices) {
    return { supported: false, status: MICROPHONE_CHECK_STATUS.UNSUPPORTED }
  }
  if (!(browserWindow.AudioContext || browserWindow.webkitAudioContext)) {
    return { supported: false, status: MICROPHONE_CHECK_STATUS.UNSUPPORTED }
  }
  return { supported: true, status: MICROPHONE_CHECK_STATUS.IDLE }
}

function wait(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      const error = new Error('Microphone check cancelled')
      error.name = 'AbortError'
      reject(error)
      return
    }

    const handleAbort = () => {
      clearTimeout(timer)
      const error = new Error('Microphone check cancelled')
      error.name = 'AbortError'
      reject(error)
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', handleAbort)
      resolve()
    }, ms)
    signal?.addEventListener('abort', handleAbort, { once: true })
  })
}

async function detectAudioSignal(analyser, { signal, durationMs }) {
  const samples = new Uint8Array(analyser.fftSize)
  const deadline = Date.now() + durationMs

  while (Date.now() < deadline) {
    analyser.getByteTimeDomainData(samples)
    let peak = 0
    for (let index = 0; index < samples.length; index += 1) {
      peak = Math.max(peak, Math.abs(samples[index] - 128) / 128)
    }
    if (peak >= SIGNAL_THRESHOLD) return true
    await wait(SAMPLE_INTERVAL_MS, signal)
  }

  return false
}

function mapMicrophoneError(error, cancelled) {
  if (cancelled) return MICROPHONE_CHECK_STATUS.CANCELLED
  if (error?.name === 'AbortError') return MICROPHONE_CHECK_STATUS.UNAVAILABLE
  if (['NotAllowedError', 'PermissionDeniedError'].includes(error?.name)) {
    return MICROPHONE_CHECK_STATUS.DENIED
  }
  if (['NotFoundError', 'DevicesNotFoundError', 'OverconstrainedError'].includes(error?.name)) {
    return MICROPHONE_CHECK_STATUS.NO_DEVICE
  }
  if (['NotReadableError', 'TrackStartError'].includes(error?.name)) {
    return MICROPHONE_CHECK_STATUS.UNAVAILABLE
  }
  if (error?.name === 'SecurityError') return MICROPHONE_CHECK_STATUS.INSECURE
  return MICROPHONE_CHECK_STATUS.ERROR
}

export async function inspectMicrophone({
  browserWindow = typeof window === 'undefined' ? null : window,
  browserNavigator = typeof navigator === 'undefined' ? null : navigator,
  signal,
  durationMs = DEFAULT_SAMPLE_DURATION_MS,
} = {}) {
  let stream = null
  let audioContext = null
  let source = null
  const startedAt = Date.now()

  try {
    if (!browserWindow || !browserNavigator?.mediaDevices) {
      return { status: MICROPHONE_CHECK_STATUS.UNSUPPORTED }
    }

    stream = await browserNavigator.mediaDevices.getUserMedia({
      audio: {
        autoGainControl: true,
        echoCancellation: true,
        noiseSuppression: true,
      },
      video: false,
    })

    if (signal?.aborted) return { status: MICROPHONE_CHECK_STATUS.CANCELLED }

    const audioTracks = stream?.getAudioTracks?.() || []
    const liveTrack = audioTracks.find((track) => track.readyState === 'live')
    const devices = await browserNavigator.mediaDevices.enumerateDevices()
    const audioInputs = (devices || []).filter((device) => device.kind === 'audioinput')

    if (!liveTrack || audioInputs.length === 0) {
      return {
        status: MICROPHONE_CHECK_STATUS.NO_DEVICE,
        audioInputCount: audioInputs.length,
        checkDurationMs: Date.now() - startedAt,
      }
    }

    const AudioContext = browserWindow.AudioContext || browserWindow.webkitAudioContext
    audioContext = new AudioContext()
    if (audioContext.state === 'suspended') await audioContext.resume()

    const analyser = audioContext.createAnalyser()
    analyser.fftSize = 512
    source = audioContext.createMediaStreamSource(stream)
    source.connect(analyser)

    const hasSignal = await detectAudioSignal(analyser, { signal, durationMs })
    if (!hasSignal) {
      return {
        status: MICROPHONE_CHECK_STATUS.NO_SIGNAL,
        audioInputCount: audioInputs.length,
        deviceLabel: liveTrack.label || audioInputs[0]?.label || '',
        checkDurationMs: Date.now() - startedAt,
      }
    }

    return {
      status: MICROPHONE_CHECK_STATUS.READY,
      deviceLabel: liveTrack.label || audioInputs[0]?.label || '마이크',
      audioInputCount: audioInputs.length,
      checkDurationMs: Date.now() - startedAt,
    }
  } catch (error) {
    return {
      status: mapMicrophoneError(error, signal?.aborted),
      checkDurationMs: Date.now() - startedAt,
    }
  } finally {
    source?.disconnect?.()
    if (audioContext && audioContext.state !== 'closed') {
      try {
        await audioContext?.close?.()
      } catch (error) {
        // The browser may already be closing the audio context.
      }
    }
    stream?.getTracks?.().forEach((track) => track.stop())
  }
}
